import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { AlertState } from 'global/AlertState'
import callApi from 'services/api'
import Modal from 'utils/Modal'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { loadingState } from 'global/LoaderState'
// Import child forms
import FIRForm from './FIRForm'
import DEForm from './DEForm'
import ACRForm from './ACRForm'

import {
  FIR_INITIAL_VALUES,
  FIR_VALIDATION_SCHEMA,
  DE_INITIAL_VALUES,
  DE_VALIDATION_SCHEMA,
  ACR_INITIAL_VALUES,
  ACR_VALIDATION_SCHEMA,
} from './Schema_InitialValues'

// Constants
const DELINQUENCY_TYPES = {
  FIR: 'fir',
  DE: 'de',
  ACR: 'acr',
}

const DELINQUENCY_OPTIONS = [
  { value: '', label: 'Select Delinquency' },
  { value: DELINQUENCY_TYPES.FIR, label: 'Criminal Case' },
  { value: DELINQUENCY_TYPES.DE, label: 'Departmental Action' },
  { value: DELINQUENCY_TYPES.ACR, label: 'Adverse Entry in ACR' },
]

// Helper function to get initial values based on type
const getInitialValues = (type, fetchedData = null) => {
  const baseValues = { delinquency: type || '' }

  if (fetchedData) {
    return {
      ...baseValues,
      ...fetchedData,
    }
  }

  switch (type) {
    case DELINQUENCY_TYPES.FIR:
      return {
        delinquency: DELINQUENCY_TYPES.FIR,
        ...FIR_INITIAL_VALUES,
      }
    case DELINQUENCY_TYPES.DE:
      return {
        delinquency: DELINQUENCY_TYPES.DE,
        ...DE_INITIAL_VALUES,
      }
    case DELINQUENCY_TYPES.ACR:
      return {
        delinquency: DELINQUENCY_TYPES.ACR,
        ...ACR_INITIAL_VALUES,
      }
    default:
      return baseValues
  }
}

// FIXED: Helper function to get validation schema based on type
const getValidationSchema = (type) => {
  const baseSchemaFields = {
    delinquency: Yup.string().required('Please select a delinquency type'),
  }
  switch (type) {
    case DELINQUENCY_TYPES.FIR:
      // Merge base schema with FIR schema
      const firMergedSchema = Yup.object().shape({
        ...baseSchemaFields,
        ...FIR_VALIDATION_SCHEMA.fields,
      })
      return firMergedSchema
    case DELINQUENCY_TYPES.DE:
      const deMergedSchema = Yup.object().shape({
        ...baseSchemaFields,
        ...DE_VALIDATION_SCHEMA.fields,
      })
      return deMergedSchema
    case DELINQUENCY_TYPES.ACR:
      const acrMergedSchema = Yup.object().shape({
        ...baseSchemaFields,
        ...ACR_VALIDATION_SCHEMA.fields,
      })
      return acrMergedSchema
    default:
      return Yup.object().shape(baseSchemaFields)
  }
}

const prepareFormData = (values, editingData, isEdit, empId) => {

  const formData = new FormData()
  const { delinquency, ...formFields } = values
  const filteredFormFields = Object.fromEntries(
    Object.entries(formFields).filter(
      ([_, v]) => v !== '' && v !== null && v !== undefined
    )
  )


  let payload
  if (isEdit) {
    payload = {
      typeOfdeliquency: delinquency,
      data: filteredFormFields,
    }
  } else {
    payload = {
      typeOfdeliquency: delinquency,
      empId: empId.id,
      data: filteredFormFields,
    }
  }

  // Add ID for update operations
  if (isEdit && editingData) {
    payload.id = editingData
  }

  switch (delinquency) {
    case DELINQUENCY_TYPES.FIR:
      formData.append('req_de', JSON.stringify(payload))
      return {
        formData,
        endpoint: isEdit ? `deliquency/${editingData}` : 'deliquency/',
      }
    case DELINQUENCY_TYPES.DE:
      formData.append('req_de', JSON.stringify(payload))
      return {
        formData,
        endpoint: isEdit ? `deliquency/${editingData}` : 'deliquency/',
      }
    case DELINQUENCY_TYPES.ACR:
      formData.append('req_de', JSON.stringify(payload))
      return {
        formData,
        endpoint: isEdit ? `deliquency/${editingData}` : 'deliquency/',
      }
    default:
      throw new Error('Invalid delinquency type')
  }
}





const AddDelinquency = ({
  showModal,
  handleCloseModal,
  editingData,
  empId,
  onSuccess,
}) => {
  const [selectedType, setSelectedType] = useState('')
  const [fetchedData, setFetchedData] = useState(null)
  const [, setAlertMessage] = useRecoilState(AlertState)
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(false)
  const [error, setError] = useState(null)

  const loader = useSetRecoilState(loadingState)
  const navigate = useNavigate()

  // Check if we're in edit mode
  const isEditMode = editingData



  const fetchDelinquencyData = async () => {
    if (!isEditMode) return

    setFetchLoading(true)
    try {
      const response = await callApi(
        `deliquency/${editingData}`,
        'GET',
        null,
        true,
        true,
        setAlertMessage,
        navigate
      )

      if (response?.success && response?.body?.data) {
        const data = {
          ...response?.body?.data,
        }
        // Exclude null or undefined values
        const filteredData = Object.fromEntries(
          Object.entries(data).filter(([_, value]) => value != null)
        )

        const payload = {
          data: filteredData,
          typeOfdeliquency: response?.body?.typeOfdeliquency,
          empId: response?.body?.empId,
        }

        setFetchedData(filteredData)
        setSelectedType(payload?.typeOfdeliquency || data.delinquency)
      }
    } catch (error) {
      setAlertMessage('data not found')
    } finally {
      setFetchLoading(false)
    }
  }

  // Fetch data when in edit mode
  useEffect(() => {
    if (showModal) {
      fetchDelinquencyData()
    }
  }, [showModal, isEditMode, editingData, setAlertMessage, navigate])

  // Memoized values
  const initialValues = useMemo(
    () => getInitialValues(selectedType, fetchedData),
    [selectedType, fetchedData]
  )

  const validationSchema = useMemo(
    () => getValidationSchema(selectedType),
    [selectedType]
  )

  // Form submission handler
  const handleSubmit = useCallback(
    async (values, { resetForm }) => {

      setLoading(true)

      formik.setTouched(
        {
          delinquency: true,
          ...Object.keys(values).reduce((acc, key) => {
            if (key !== 'delinquency') acc[key] = true
            return acc
          }, {}),
        },
        true
      )

      try {
        const { formData, endpoint } = prepareFormData(
          values,
          editingData,
          isEditMode,
          empId
        )

        const response = await callApi(
          endpoint,
          isEditMode ? 'PUT' : 'POST',
          formData,
          false,
          true,
          setAlertMessage,
          navigate,
          null
        )

        if (isEditMode && response?.success) {
          onSuccess()
        }
        if (response?.success) {
          resetForm()
          onSuccess(empId)
          setSelectedType('')
          setFetchedData(null)
          handleCloseModal()
        }
      } catch (error) {
        setAlertMessage(
          `An error occurred while ${isEditMode ? 'updating' : 'submitting'
          } the form. Please try again.`
        )
      } finally {
        setLoading(false)
      }
    },
    [setAlertMessage, navigate, handleCloseModal, isEditMode, editingData]
  )

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    formik.setFieldValue('delinquency', selectedType)
  }, [selectedType])

  const handleDelinquencyChange = useCallback(
    (e) => {
      const value = e.target.value
      setSelectedType(value)
      formik.setFieldValue('delinquency', value)
      formik.setFieldTouched('delinquency', true, true)
    },
    [formik]
  )

  const renderChildForm = () => {
    if (fetchLoading) {
      return <div className="text-center py-3">Loading form data...</div>
    }

    switch (selectedType) {
      case DELINQUENCY_TYPES.FIR:
        return <FIRForm formik={formik} />
      case DELINQUENCY_TYPES.DE:
        return <DEForm formik={formik} />
      case DELINQUENCY_TYPES.ACR:
        return <ACRForm formik={formik} />
      default:
        return null
    }
  }

  const handleClose = () => {
    formik.resetForm()
    setSelectedType('')
    setFetchedData(null)
    handleCloseModal()
  }

  // Add a test button

  return (
    <Modal
      showModal={showModal}
      handleCloseModal={handleClose}
      title={isEditMode ? 'Edit Delinquency' : 'Add Delinquency'}
      modalWidth={{ width: '800px' }}
    >
      <form onSubmit={formik.handleSubmit}>
        {/* Select field */}
        <div className="form-group row mb-3">
          {
            formik.values.delinquency === DELINQUENCY_TYPES.ACR &&
            <strong className='text-danger mb-4'>
              Note : Details of only those ACRs to be entered in which integrity or reliability have been remarked adverse.
            </strong>
          }
          <label className="col-sm-4 col-form-label ">
            Type of Delinquency <span style={{ color: 'red' }}>*</span>
          </label>
          <div className="col-sm-8">
            <select
              className={` text-uppercase form-control ${formik.touched.delinquency && formik.errors.delinquency
                ? 'is-invalid'
                : ''
                }`}
              name="delinquency"
              value={formik.values.delinquency}
              onChange={handleDelinquencyChange}
              onBlur={formik.handleBlur}
              disabled={fetchLoading}
            >

              {DELINQUENCY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {formik.touched.delinquency && formik.errors.delinquency && (
              <div className="invalid-feedback d-block">
                {formik.errors.delinquency}
              </div>
            )}
          </div>
        </div>

        {/* Conditional child forms */}
        {renderChildForm()}

        {/* Buttons */}
        <div className="d-flex justify-content-center mt-4">
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={handleClose}
            disabled={loading || fetchLoading}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || fetchLoading || !selectedType}
          >
            {loading
              ? isEditMode
                ? 'Updating...'
                : 'Submitting...'
              : isEditMode
                ? 'Update'
                : 'Submit'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default AddDelinquency
